import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'redux-bundler-react';
import { createColumnHelper } from '@tanstack/react-table';
import { Accordion, Alert, Label } from '@trussworks/react-uswds';

import { EditCell } from '@components/table/tableCellComponents/EditCell';
import { TableCell } from '@components/table/tableCellComponents/TableCell';
import { PermitDateTableCell } from '@components/table/tableCellComponents/PermitDateTableCell';

import StepHeader from '@components/step-header/StepHeader';
import TextAreaInput from '@components/textarea/TextArea';
import SelectInput from '@components/select/Select';
import TanStackTableNew from '@components/table/TanStackTable/TanStackTableNew';
import ExternalLink from '@components/external-link/ExternalLink';
import Tooltip from '@components/tooltip/tooltip';
import MultiSelectInput from '@components/multi-select/multiSelectInput';
import ExplanationTableCell from '@components/table/tableCellComponents/ExplanationTableCell';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Checkbox from '@components/checkbox/Checkbox';
import Section from '@components/section/Section';
import PermitsDredgeProject from './PermitsFormGeneralProjectInformation.dredgeProject';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import _isEqual from 'lodash/isEqual';

import { decimalNumberRegex, integerRegex, latRegex, lngRegex, setNumberValue } from '@src/utils/regex';

import { ApplicableStatutoryAuthorities } from '@src/utils/enums';
import { dateBeforeB, filterNullEmptyObjects, formatCoordFlt, isDateValid } from '@src/utils/helpers';
import { useRequest } from '@stores';
import { useResetDirtyFields } from '@hooks';

export const PcnPermitInformationMetadata = {
  sectionName: 'Permit Information',
};

const currentEnv = import.meta.env.VITE_ENVIRONMENT;

const nwpTermsList = [
  {
    term: 'NWP 3, Maintenance',
    desc: 'information regarding the original design capacities and configurations of the outfalls, intakes, small impoundments, and canals',
  },
  {
    term: 'NWP 31, Maintenance of Existing Flood Control Facilities',
    desc: 'a description of the maintenance baseline and the dredged material disposal site',
  },
  {
    term: 'NWP 33, Temporary Construction, Access, and Dewatering',
    desc: 'a restoration plan showing how all temporary fills and structures will be removed and the area restored to pre-project conditions',
  },
  {
    term: 'NWP 44, Mining Activities',
    desc: 'if reclamation is required by other statutes, then a copy of the final reclamation plan must be submitted with the preconstruction notification',
  },
  {
    term: 'NWP 45, Repair of Uplands Damaged by Discrete Events',
    desc: 'documentation, such as a recent topographic survey or photographs, to justify the extent of the proposed restoration',
  },
  {
    term: 'NWP 48, Commercial Shellfish Aquaculture Activities',
    desc: '(1) a map showing the boundaries of the project area, with latitude and longitude coordinates for each corner of the project area; (2) the name(s) of the species that will be cultivated during the period this NWP is in effect; (3) whether canopy predator nets will be used; (4) whether suspended cultivation techniques will be used; and (5) general water depths in the project area (a detailed survey is not required)',
  },
  {
    term: 'NWP 49, Coal Remaining Activities',
    desc: 'a document describing how the overall mining plan will result in a net increase in aquatic resource functions must be submitted to the district engineer and receive written authorization prior to commencing the activity',
  },
  {
    term: 'NWP 50, Underground Coal Mining Activities',
    desc: 'if reclamation is required by other statutes, then a copy of the reclamation plan must be submitted with the preconstruction notification',
  },
];

const nwpTermsAccordionList = [
  {
    title:
      'The terms of some of the Nationwide Permits include additional information requirements for preconstruction notifications',
    content: (
      <>
        {nwpTermsList.map((nwp, index) => (
          <p key={index} className={`margin-bottom-0 ${index !== 0 && 'margin-top-105'}`}>
            <span className='text-bold'>{nwp.term}</span> - {nwp.desc}.
          </p>
        ))}
      </>
    ),
    id: '1',
    headingLevel: 'h3',
  },
];

const PcnPermitInformation = connect(
  'doUpdatePermitRequest',
  'doUpdatePermitRequestSub',
  'selectGPPermitTypes',
  'selectPermitData',
  'selectRequestAPIData',
  ({ doUpdatePermitRequest, doUpdatePermitRequestSub, gPPermitTypes, permitData, requestAPIData, stepNo }) => {
    const [otherNWPData, setOtherNWPData] = useState([]);
    const [otherNWPKey, setOtherNWPKey] = useState(0);
    const [tableInvalid, setTableInvalid] = useState(true);
    const [tableIsDirty, setTableIsDirty] = useState(false);
    const [rowErrors, setRowErrors] = useState(false);
    const [dredgeProjectData, setDredgeProjectData] = useState({});

    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched } = useRequest();

    const columnHelper = createColumnHelper();

    const prevOtherNWPDataRef = useRef([]);

    const populateDredgeTypes = useCallback((permitAPIData) => {
      const dredgeTypes = [];
      if (permitAPIData?.dredgeProject?.isDredgeTypeMaintenance) {
        dredgeTypes.push('Maintenance');
      }
      if (permitAPIData?.dredgeProject?.isDredgeTypeNew) {
        dredgeTypes.push('New');
      }
      return dredgeTypes;
    }, []);

    const otherNWPValidationSchema = yup.object().shape({
      approvalType: yup.string().required('Please select an option'),
      explanation: yup
        .string()
        .when('approvalType', { is: 'Other', then: () => yup.string().required('Field is required') })
        .nullable(),
      fileNumber: yup.string().required('Field is required'),
      appliedDate: yup.string().required('Please select a date'),
      approvedDate: yup.string().nullable(),
      deniedDate: yup.string().nullable(),
    });

    const otherNWPTableLength = otherNWPData?.length;

    const schema = (otherNWPTableLength, rowErrors) =>
      yup.object().shape(
        {
          gpPermitTypes: yup.array().min(1, 'Please select an option'),
          otherNWP: yup
            .boolean()
            .test(
              'check-table-length-and-errors',
              'Other Nationwide Permits: Table contains invalid rows',
              function () {
                return !(rowErrors && otherNWPTableLength > 0);
              }
            ),
          applicableStatutoryAuthority: yup.array().min(1, 'Please select at least one value'),
          navigableWatersAdditionalInfo: yup.string().nullable(),
          isAnyWorkComplete: yup.string().required('Please select an option'),
          completedWorkDescription: yup
            .string()
            .when('isAnyWorkComplete', { is: 'true', then: () => yup.string().required('Field is required') })
            .nullable(),
          //DREDGING
          isDredgingNavWaters: yup
            .string()
            .when('applicableStatutoryAuthority', {
              is: (val) => val?.includes(ApplicableStatutoryAuthorities.Section10),
              then: () => yup.string().required('Please select an option'),
            })
            .nullable(),
          materialTypeComposition: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Field is required') })
            .nullable(),
          dredgeMethod: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Field is required') })
            .nullable(),
          constructionMethods: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Field is required') })
            .nullable(),
          estimatedNumberOfEvents: yup
            .string()
            .when(['estimatedNumberOfEvents', 'isDredgingNavWaters'], (estimatedNumberOfEvents, isDredgingNavWaters) =>
              estimatedNumberOfEvents && isDredgingNavWaters === 'true'
                ? yup.string().matches(integerRegex, { message: 'Must be an integer.', excludeEmptyString: true })
                : yup.string().nullable()
            ),
          workStartDate: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Please select a date') })
            .nullable(),
          workEndDate: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Please select a date') })
            .nullable()
            .test({
              test: (value, { parent: { isDredgingNavWaters } }) =>
                isDredgingNavWaters === 'true' ? value !== '' : true,
              message: 'Please select a date',
            })
            .test({
              test: (value, { parent: { workStartDate } }) => !value || new Date(value) >= new Date(workStartDate),
              message: 'The end date must be after the start date',
            }),
          durationOfWork: yup
            .string()
            .when('isDredgingNavWaters', {
              is: 'true',
              then: () =>
                yup
                  .number()
                  .required('Field is required')
                  .typeError('Must be an integer.')
                  .positive('Value must be > 0'),
            })
            .nullable(),
          unitOfWork: yup
            .string()
            .when('isDredgingNavWaters', { is: 'true', then: () => yup.string().required('Please select an option') })
            .nullable(),
          currentDepth: yup
            .string()
            .when('isDredgingNavWaters', {
              is: 'true',
              then: () => yup.string().required('Field is required').matches(decimalNumberRegex, 'Field is invalid'),
            })
            .nullable(),
          dredgeType: yup
            .array()
            .when('isDredgingNavWaters', {
              is: 'true',
              then: () => yup.array().min(1, 'Please select at least one value'),
            })
            .nullable(),
          lastDredgeCycle: yup
            .string()
            .when('dredgeType', {
              is: (val) => val?.includes('Maintenance'),
              then: () => yup.string().required('Please select a date'),
            })
            .nullable(),
          authorizedLength: yup
            .string()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () => yup.string().required('Field is required').matches(decimalNumberRegex, 'Field is invalid'),
            })
            .nullable(),
          authorizedWidth: yup
            .string()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () => yup.string().required('Field is required').matches(decimalNumberRegex, 'Field is invalid'),
            })
            .nullable(),
          authorizedDepth: yup
            .string()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () => yup.string().required('Field is required').matches(decimalNumberRegex, 'Field is invalid'),
            })
            .nullable(),
          disposalPlan: yup
            .string()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () => yup.string().required('Field is required'),
            })
            .nullable(),
          estNumDisposalTrips: yup
            .string()
            .when(['estNumDisposalTrips', 'dredgeType'], (estNumDisposalTrips, dredgeType) =>
              estNumDisposalTrips && dredgeType?.length > 0
                ? yup.string().matches(integerRegex, { message: 'Must be an integer.', excludeEmptyString: true })
                : yup.string().nullable()
            ),
          dredgeDisposalLatitude: yup
            .mixed()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () =>
                yup
                  .string()
                  .required('Field is required')
                  .matches(latRegex, 'Format is incorrect. Must be +-XX.XXXXXX and include at least 6 decimal places.'),
            })
            .nullable(),
          dredgeDisposalLongitude: yup
            .mixed()
            .when('dredgeType', {
              is: (val) => val?.length > 0,
              then: () =>
                yup
                  .string()
                  .required('Field is required')
                  .matches(
                    lngRegex,
                    'Format is incorrect. Must be +-XXX.XXXXXX and include at least 6 decimal places.'
                  ),
            })
            .nullable(),
        },
        [
          ['estimatedNumberOfEvents', 'estimatedNumberOfEvents'],
          ['estNumDisposalTrips', 'estNumDisposalTrips'],
          ['existingVesselSlips', 'existingVesselSlips'],
        ]
      );

    const defaultValues = {
      applicableStatutoryAuthority: permitData?.applicableStatutoryAuthorities ?? [],
      gpPermitTypes:
        permitData?.gpPermitTypes?.map((item) => ({
          value: String(item.permitTypeID),
          label: item.permitType ? `${item.value} (${item.permitType})` : item.value,
        })) ?? [],
      supportingPermits: permitData?.supportingPermits
        ? permitData?.supportingPermits?.map((item) => ({
            ...item,
            appliedDate: item?.appliedDate?.split('T')?.[0],
            approvedDate: item?.approvedDate?.split('T')?.[0],
            deniedDate: item?.deniedDate?.split('T')?.[0],
          }))
        : [],
      activityDescription: permitData?.activityDescription ?? '',
      activityPurposeAndNeed: permitData?.activityPurposeAndNeed ?? '',
      navigableWatersAdditionalInfo: permitData?.navigableWatersAdditionalInfo ?? '',
      isAnyWorkComplete:
        permitData?.isAnyWorkComplete !== undefined && permitData?.isAnyWorkComplete !== null
          ? permitData.isAnyWorkComplete
            ? 'true'
            : 'false'
          : '',
      completedWorkDescription: permitData?.completedWorkDescription ?? '',
      // Dredge Project
      isDredgingNavWaters:
        permitData?.isDredgingNavWaters !== undefined && permitData?.isDredgingNavWaters !== null
          ? permitData.isDredgingNavWaters
            ? 'true'
            : 'false'
          : '',
      materialTypeComposition: permitData?.dredgeProject?.materialTypeComposition ?? '',
      dredgeMethod: permitData?.dredgeProject?.dredgeMethod ?? '',
      constructionMethods: permitData?.dredgeProject?.constructionMethods ?? '',
      estimatedNumberOfEvents: permitData?.dredgeProject?.estimatedNumberOfEvents ?? '',
      workStartDate: permitData?.dredgeProject?.workStartDate?.split('T')?.[0] ?? '',
      workEndDate: permitData?.dredgeProject?.workEndDate?.split('T')?.[0] ?? '',
      durationOfWork: permitData?.dredgeProject?.durationOfWork ?? null,
      unitOfWork: permitData?.dredgeProject?.unitOfWork ?? '',
      currentDepth: permitData?.dredgeProject?.currentDepth ?? '',
      dredgeType: populateDredgeTypes(permitData),
      lastDredgeCycle: permitData?.dredgeProject?.lastDredgeCycle?.split('T')?.[0] ?? '',
      authorizedLength: permitData?.dredgeProject?.authorizedLength ?? '',
      authorizedWidth: permitData?.dredgeProject?.authorizedWidth ?? '',
      authorizedDepth: permitData?.dredgeProject?.authorizedDepth ?? '',
      disposalPlan: permitData?.dredgeProject?.disposalPlan ?? '',
      estNumDisposalTrips: permitData?.dredgeProject?.estNumDisposalTrips ?? '',
      dredgeDisposalLatitude:
        typeof permitData?.dredgeProject?.dredgeDisposalLatitude === 'number'
          ? permitData?.dredgeProject?.dredgeDisposalLatitude?.toFixed(7)
          : (permitData?.dredgeProject?.dredgeDisposalLatitude ?? ''),
      dredgeDisposalLongitude:
        typeof permitData?.dredgeProject?.dredgeDisposalLongitude === 'number'
          ? permitData?.dredgeProject?.dredgeDisposalLongitude?.toFixed(7)
          : (permitData?.dredgeProject?.dredgeDisposalLongitude ?? ''),

      // Pile Driving and Coastal Shoreline Stabilization Manual Overwrite
      includesInstallPiling:
        permitData?.includesInstallPiling !== undefined && permitData?.includesInstallPiling !== ''
          ? permitData.includesInstallPiling
            ? 'true'
            : 'false'
          : 'false',
      includesShorelineStabilization:
        permitData?.includesShorelineStabilization !== undefined && permitData?.includesShorelineStabilization !== ''
          ? permitData.includesShorelineStabilization
            ? 'true'
            : 'false'
          : 'false',
    };

    const methods = useForm({
      resolver: yupResolver(schema(otherNWPTableLength, rowErrors)),
      mode: 'onBlur',
      defaultValues: defaultValues,
    });
    const {
      formState: { errors, dirtyFields },
      setValue,
      setError,
      clearErrors,
      watch,
      getValues,
      trigger,
      reset,
    } = methods;

    const isAnyWorkComplete = watch('isAnyWorkComplete');
    const applicableStatutoryAuthority = watch('applicableStatutoryAuthority');
    const isDredgingNavWaters = watch('isDredgingNavWaters');
    const dredgeType = watch('dredgeType');
    const gpPermitTypes = watch('gpPermitTypes');
    const isDirty = Object.keys(dirtyFields).length > 0 || tableIsDirty;
    const isValid = Object.keys(errors).length === 0 && !tableInvalid;
    const isTouched = isSectionTouched(stepNo);

    useEffect(() => {
      updateSection(stepNo, { dirty: isDirty, valid: isValid });
    }, [stepNo, isDirty, isValid, updateSection]);

    useEffect(() => {
      const tableDataChanged = !_isEqual(otherNWPData, prevOtherNWPDataRef.current);
      tableDataChanged && setTableIsDirty(true);
    }, [otherNWPData]);

    //Reset the dirty states of the fields after a save.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    const NWPColumns = useMemo(
      () => [
        columnHelper.display({
          id: 'edit',
          cell: isReadOnly ? <></> : EditCell,
          size: 40,
          enableResizing: false,
          enableSorting: false,
        }),
        columnHelper.accessor('approvalType', {
          header: 'Type of Approval',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 275,
          meta: {
            type: 'select',
            required: true,
            options: [
              { value: 'Nationwide Permit', label: 'Nationwide Permit' },
              { value: 'Regional General Permit', label: 'Regional General Permit' },
              { value: 'Programmatic General Permit', label: 'Programmatic General Permit' },
              { value: 'Individual Permit', label: 'Individual Permit' },
              { value: 'Other', label: 'Other' },
            ],
          },
        }),
        columnHelper.accessor('explanation', {
          header: 'Explanation',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : ExplanationTableCell,
          size: 300,
          meta: {
            required: true,
            type: 'text',
          },
        }),
        columnHelper.accessor('fileNumber', {
          header: 'File Number',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : TableCell,
          size: 300,
          meta: {
            required: true,
            type: 'text',
          },
        }),
        columnHelper.accessor('appliedDate', {
          header: 'Date Applied',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: true,
          },
        }),
        columnHelper.accessor('approvedDate', {
          header: 'Date Approved',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: false,
          },
        }),
        columnHelper.accessor('deniedDate', {
          header: 'Date Denied',
          cell: isReadOnly ? ({ cell }) => <span>{cell.getValue()}</span> : PermitDateTableCell,
          size: 150,
          meta: {
            type: 'date',
            min: dateBeforeB,
            required: false,
          },
        }),
      ],
      [columnHelper, isReadOnly]
    );

    const updateOtherNWPData = useCallback(
      (rowIndex, columnId, updatedValue) => {
        setOtherNWPData((oldData) => {
          const newData = oldData ? [...oldData] : null;
          if (newData && newData[rowIndex]) {
            // Update properties
            newData[rowIndex] = {
              ...newData[rowIndex],
              [columnId]: updatedValue,
            };
            return newData;
          }
        });
      },
      [setOtherNWPData]
    );

    const removeMultipleRows = useCallback(
      (indicesToRemove) => {
        setOtherNWPData((oldData) => {
          const newRows = oldData.filter((_, index) => !indicesToRemove.includes(index));
          return newRows;
        });
        setOtherNWPKey((old) => old + 1);
      },
      [setOtherNWPData, setOtherNWPKey]
    );

    // Update Supporting Permits data in Permit Request Body
    useEffect(() => {
      otherNWPData &&
        doUpdatePermitRequest({
          supportingPermits: otherNWPData.map((item) => ({
            ...item,
            appliedDate:
              isDateValid(item?.appliedDate?.split('T')?.[0]) === true ? item?.appliedDate?.split('T')?.[0] : '',
            approvedDate:
              isDateValid(item?.approvedDate?.split('T')?.[0]) === true ? item?.approvedDate?.split('T')?.[0] : '',
            deniedDate:
              isDateValid(item?.deniedDate?.split('T')?.[0]) === true ? item?.deniedDate?.split('T')?.[0] : '',
          })),
        });
    }, [otherNWPData, doUpdatePermitRequest]);

    // Update Dredge Project data in Permit Request Body
    useEffect(() => {
      dredgeProjectData && doUpdatePermitRequest({ dredgeProject: dredgeProjectData });
    }, [dredgeProjectData, doUpdatePermitRequest]);

    // Load data from API
    useEffect(() => {
      if (requestAPIData?.request?.permits?.[0]?.supportingPermits) {
        const otherNWPData = requestAPIData?.request?.permits?.[0]?.supportingPermits.map((item) => ({
          ...item,
          appliedDate:
            isDateValid(item?.appliedDate?.split('T')?.[0]) === true ? item?.appliedDate?.split('T')?.[0] : '',
          approvedDate:
            isDateValid(item?.approvedDate?.split('T')?.[0]) === true ? item?.approvedDate?.split('T')?.[0] : '',
          deniedDate: isDateValid(item?.deniedDate?.split('T')?.[0]) === true ? item?.deniedDate?.split('T')?.[0] : '',
        }));

        setOtherNWPData(otherNWPData);
        prevOtherNWPDataRef.current = otherNWPData;
      }

      if (requestAPIData?.request?.permits?.[0]?.dredgeProject) {
        setDredgeProjectData(requestAPIData?.request?.permits?.[0]?.dredgeProject);
      }
      isTouched && trigger();
    }, [requestAPIData, isTouched, trigger]);

    const handleChange = () => {
      const populateDredgeProject = () => {
        const values = getValues();
        // Dredge Project
        const dredgeProjectObj = {
          materialTypeComposition: values?.materialTypeComposition ?? '',
          dredgeMethod: values?.dredgeMethod ?? '',
          constructionMethods: values?.constructionMethods ?? '',
          estimatedNumberOfEvents: setNumberValue(values?.estimatedNumberOfEvents),
          workStartDate: values?.workStartDate ?? '',
          workEndDate: values?.workEndDate ?? '',
          durationOfWork: setNumberValue(values?.durationOfWork),
          unitOfWork: values?.unitOfWork ?? '',
          currentDepth: setNumberValue(values?.currentDepth, false),
          isDredgeTypeMaintenance:
            values?.dredgeType.length > 0 ? (values?.dredgeType?.includes('Maintenance') ? true : false) : null,
          isDredgeTypeNew: values?.dredgeType.length > 0 ? (values?.dredgeType?.includes('New') ? true : false) : null,
          lastDredgeCycle: values?.lastDredgeCycle ?? '',
          authorizedLength: setNumberValue(values?.authorizedLength, false),
          authorizedWidth: setNumberValue(values?.authorizedWidth, false),
          authorizedDepth: setNumberValue(values?.authorizedDepth, false),
          disposalPlan: values?.disposalPlan ?? '',
          estNumDisposalTrips: setNumberValue(values?.estNumDisposalTrips),
          dredgeDisposalLatitude: formatCoordFlt(values?.dredgeDisposalLatitude) ?? null,
          dredgeDisposalLongitude: formatCoordFlt(values?.dredgeDisposalLongitude) ?? null,
        };
        // Check if values in obj are all null/empty string
        return filterNullEmptyObjects(dredgeProjectObj) !== null ? { ...dredgeProjectData, ...dredgeProjectObj } : null;
      };

      // Map & insert values into GP data modal
      if (!isReadOnly) {
        const values = getValues();
        const permitTypes = values?.gpPermitTypes?.map((val) => val.value);
        const gpValues = {
          activityDescription: values?.activityDescription ?? '',
          activityPurposeAndNeed: values?.activityPurposeAndNeed ?? '',
          navigableWatersAdditionalInfo: values?.navigableWatersAdditionalInfo ?? '',
          isAnyWorkComplete:
            values?.isAnyWorkComplete === 'true' ? true : values?.isAnyWorkComplete === 'false' ? false : null,
          completedWorkDescription: values?.completedWorkDescription ?? '',
          isDredgingNavWaters:
            values?.isDredgingNavWaters === 'true' ? true : values?.isDredgingNavWaters === 'false' ? false : null,
          includesInstallPiling: false,
          includesShorelineStabilization: false,
          applicableStatutoryAuthorities: values?.applicableStatutoryAuthority ?? [],
          gpPermitTypes: permitTypes,
          dredgeProject: populateDredgeProject() ?? null,
        };
        doUpdatePermitRequest(gpValues);
      }
    };

    // Populate General Project Info nested sections

    const populateGPPermitTypeOptions = () => {
      if (gpPermitTypes.find((item) => item.value === "I'm not sure")) {
        return [{ value: "I'm not sure", label: "I'm not sure" }];
      } else if (gpPermitTypes.length > 0 && gpPermitTypes.find((item) => item.value !== "I'm not sure")) {
        return gPPermitTypes?.map((item) => ({ value: item, label: item }));
      } else {
        return [
          { value: "I'm not sure", label: "I'm not sure" },
          ...gPPermitTypes?.map((item) => ({ value: item, label: item })),
        ];
      }
    };

    /* clear out sections when dropdowns are false */
    useEffect(() => {
      if (isAnyWorkComplete === 'false' || isAnyWorkComplete === '') {
        setValue('completedWorkDescription', '', { shouldValidate: isTouched });
        doUpdatePermitRequest({ completedWorkDescription: '' });
      }
    }, [isAnyWorkComplete, isTouched, setValue, doUpdatePermitRequest]);

    useEffect(() => {
      if (!applicableStatutoryAuthority.includes(ApplicableStatutoryAuthorities.Section10)) {
        setValue('isDredgingNavWaters', '', { shouldValidate: isTouched });
        doUpdatePermitRequest({ isDredgingNavWaters: null });
      }
    }, [applicableStatutoryAuthority, isTouched, doUpdatePermitRequest, setValue]);

    useEffect(() => {
      if (applicableStatutoryAuthority.length === 0) {
        setValue('isDredgingNavWaters', '', { shouldValidate: isTouched });
        doUpdatePermitRequest({ isDredgingNavWaters: null });
      }
    }, [applicableStatutoryAuthority, isTouched, doUpdatePermitRequest, setValue]);

    // Dredging
    useEffect(() => {
      if (dredgeType.length === 0) {
        setValue('lastDredgeCycle', '', { shouldValidate: isTouched });
        setValue('authorizedLength', '', { shouldValidate: isTouched });
        setValue('authorizedWidth', '', { shouldValidate: isTouched });
        setValue('authorizedDepth', '', { shouldValidate: isTouched });
        setValue('disposalPlan', '', { shouldValidate: isTouched });
        setValue('estNumDisposalTrips', '', { shouldValidate: isTouched });
        setValue('dredgeDisposalLatitude', '', { shouldValidate: isTouched });
        setValue('dredgeDisposalLongitude', '', { shouldValidate: isTouched });
        doUpdatePermitRequestSub('dredgeProject', { lastDredgeCycle: null });
        doUpdatePermitRequestSub('dredgeProject', { authorizedLength: null });
        doUpdatePermitRequestSub('dredgeProject', { authorizedWidth: null });
        doUpdatePermitRequestSub('dredgeProject', { authorizedDepth: null });
        doUpdatePermitRequestSub('dredgeProject', { disposalPlan: '' });
        doUpdatePermitRequestSub('dredgeProject', { estNumDisposalTrips: null });
        doUpdatePermitRequestSub('dredgeProject', { dredgeDisposalLatitude: null });
        doUpdatePermitRequestSub('dredgeProject', { dredgeDisposalLongitude: null });
      }
    }, [dredgeType, isTouched, setValue, doUpdatePermitRequestSub]);

    useEffect(() => {
      if (isDredgingNavWaters === 'false' || isDredgingNavWaters === '') {
        setValue('materialTypeComposition', '', { shouldValidate: isTouched });
        setValue('dredgeMethod', '', { shouldValidate: isTouched });
        setValue('constructionMethods', '', { shouldValidate: isTouched });
        setValue('estimatedNumberOfEvents', '', { shouldValidate: isTouched });
        setValue('workStartDate', '', { shouldValidate: isTouched });
        setValue('workEndDate', '', { shouldValidate: isTouched });
        setValue('durationOfWork', '', { shouldValidate: isTouched });
        setValue('unitOfWork', '', { shouldValidate: isTouched });
        setValue('currentDepth', '', { shouldValidate: isTouched });
        setValue('dredgeType', [], { shouldValidate: isTouched });
        doUpdatePermitRequestSub('dredgeProject', { materialTypeComposition: '' });
        doUpdatePermitRequestSub('dredgeProject', { dredgeMethod: '' });
        doUpdatePermitRequestSub('dredgeProject', { constructionMethods: '' });
        doUpdatePermitRequestSub('dredgeProject', { estimatedNumberOfEvents: null });
        doUpdatePermitRequestSub('dredgeProject', { workStartDate: '' });
        doUpdatePermitRequestSub('dredgeProject', { workEndDate: '' });
        doUpdatePermitRequestSub('dredgeProject', { durationOfWork: null });
        doUpdatePermitRequestSub('dredgeProject', { unitOfWork: '' });
        doUpdatePermitRequestSub('dredgeProject', { currentDepth: null });
        doUpdatePermitRequestSub('dredgeProject', { isDredgeTypeMaintenance: null });
        doUpdatePermitRequestSub('dredgeProject', { isDredgeTypeNew: null });
        // Reset all dredge project data
        setDredgeProjectData({});
      }
    }, [isDredgingNavWaters, isTouched, setValue, doUpdatePermitRequestSub]);

    useEffect(() => {
      if (isTouched) {
        if (rowErrors && otherNWPData?.length > 0) {
          setError('otherNWP', { type: 'custom', message: 'Other Nationwide Permits: Table contains invalid rows' });
          setTableInvalid(true);
        } else if (!rowErrors && tableInvalid !== false) {
          clearErrors('otherNWP');
          setTableInvalid(false);
        }
      }
    }, [rowErrors, otherNWPData.length, isTouched, setTableInvalid, setError, clearErrors, tableInvalid]);

    useEffect(() => {
      updateSection(stepNo, { valid: isReadOnly ? true : isValid });
    }, [isValid, updateSection, stepNo, tableInvalid, isReadOnly]);

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader text='Permit Information' />
        <div>
          <MultiSelectInput
            multi
            name='gpPermitTypes'
            label='Identify the specific Nationwide Permit(s) or Regional or Programmatic General Permit you propose to use'
            tooltip='List the number(s) of the Nationwide Permit(s) you want to use to authorize the proposed activity (e.g., NWP 29).'
            options={populateGPPermitTypeOptions()}
            handleChange={handleChange}
            readOnly={isReadOnly}
            required
          />
        </div>
        <Alert type='info' heading='NOTE' headingLevel='h4'>
          You can access additional information about the Nationwide Permit(s) or Regional or Programmatic General
          Permit you select above, at the following link:{' '}
          <ExternalLink
            href='https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll7/id/19757'
            title='read more here'
            content='https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll7/id/19757'
          />
        </Alert>
        <Alert type='info'>
          The PCN must include a delineation of wetlands, other special aquatic sites, and other waters, such as lakes
          and ponds, and perennial and intermittent streams, on the project site. Wetland delineations must be prepared
          in accordance with the current method required by USACE. The permittee may ask USACE to delineate the special
          aquatic sites and other waters on the project site, but there may be a delay if USACE does the delineation,
          especially if the project site is large or contains many wetlands, other special aquatic sites, and other
          waters. Furthermore, the 45-day period will not start until the delineation has been submitted to or completed
          by USACE, as appropriate.
        </Alert>
        <label className='margin-top-2 margin-bottom-0 margin-left-1'>
          List any other Nationwide Permit(s), Regional General Permit(s), Individual Permit(s) used to authorize any
          part of the proposed project or any related activity. Also, if any other Certificates or Approvals/Denials
          have been received from other Federal, State or Local Agencies for Work Described in This Application, please
          include here and select “Other” in the Type of Approval column{' '}
          <Tooltip
            name='NWP-tooltip'
            content='List any other Nationwide Permit(s), Regional General Permit(s), or individual permit(s) used or intended to be used to authorize any part of the proposed project or any related activity. For linear projects, list other separate and distant crossings of waters and wetlands authorized by Nationwide Permits 12 or 14 that do not require PCNs.'
            clickable={false}
            iconStyle={{ marginLeft: '5px' }}
          />
        </label>
        <div id='otherNWP'>
          <TanStackTableNew
            addRow={() => setOtherNWPData((prev) => (prev ? [...prev, {}] : [{}]))}
            columns={NWPColumns}
            data={otherNWPData}
            hideDrag={true}
            initialTableState={{}}
            isInputTable={true}
            isReadOnly={isReadOnly}
            key={otherNWPKey}
            placeholderClick={() => setOtherNWPData((prev) => (prev ? [...prev, {}] : [{}]))}
            placeholderText='No Permits found.'
            removeMultipleRows={removeMultipleRows}
            rowErrorCallback={setRowErrors}
            updateSourceData={updateOtherNWPData}
            validationSchema={otherNWPValidationSchema}
          />
        </div>
        <fieldset className='margin-top-2 padding-0' id='applicableStatutoryAuthority'>
          <Label className='margin-bottom-0 required' htmlFor='applicableStatutoryAuthority'>
            <span id='applicableStatutoryAuthority_label'>Select Applicable Statutory Authority</span>
          </Label>
          <Alert className='margin-top-1' type='info'>
            If you're unsure of which authorities apply to your project, you can{' '}
            <ExternalLink
              href={
                currentEnv === 'local' || currentEnv === 'development' ? '/home/permitting' : '/rrs/home/permitting'
              }
              title='read about permitting requirements'
              content='read about permitting requirements'
              aria-label='read about permitting requirements'
            />{' '}
            or contact your{' '}
            <ExternalLink
              href='https://regulatory.ops.usace.army.mil/offices '
              title='local regulatory office'
              content='local regulatory office'
              aria-label='local regulatory office'
            />
            .
          </Alert>
          <Checkbox
            disabled={isReadOnly}
            id='check-section-404'
            label={ApplicableStatutoryAuthorities.Section404}
            labelDescription='Requires authorization from the U.S. Army Corps of Engineers, for the discharge of dredged or fill material into all waters of the United States, including wetlands. Discharges of fill material generally include, without limitation: placement of fill that is necessary for the construction of any structure, or impoundment requiring rock, sand, dirt, or other material for its construction; site-development fills for recreational, industrial, commercial, residential, and other uses; causeways or road fills; dams and dikes; artificial islands; property protection or reclamation devices such as riprap, groins, seawalls, breakwaters, and revetments; beach nourishment; levees; fill for intake and outfall pipes and subaqueous utility lines; fill associated with the creation of ponds; and any other work involving the discharge of fill or dredged material. A USACE permit is required whether the work is permanent or temporary. Examples of temporary discharges include dewatering of dredged material prior to final disposal, and temporary fills for access roadways, cofferdams, storage and work areas.'
            name='applicableStatutoryAuthority'
            onChange={handleChange}
            tile
            value={ApplicableStatutoryAuthorities.Section404}
          />
          <Checkbox
            disabled={isReadOnly}
            id='check-section-10'
            label={ApplicableStatutoryAuthorities.Section10}
            labelDescription='Requires authorization from the U.S. Army Corps of Engineers for the construction of any structure in or over any navigable water of the United States. Structures or work outside the limits defined for navigable waters of the United States require a Section 10 permit if the structure or work affects the course, location, or condition of the water body. The law applies to any dredging or disposal of dredged materials, excavation, filling, re-channelization, or any other modification of a navigable water of the United States, and applies to all structures, from the smallest floating dock to the largest commercial undertaking. It further includes, without limitation, any wharf, dolphin, weir, boom breakwater, jetty, groin, bank protection (e.g. riprap, revetment, bulkhead), mooring structures such as pilings, aerial or subaqueous power transmission lines, intake or outfall pipes, permanently moored floating vessel, tunnel, artificial canal, boat ramp, aids to navigation, and any other permanent, or semi-permanent obstacle or obstruction.'
            name='applicableStatutoryAuthority'
            onChange={handleChange}
            tile
            value={ApplicableStatutoryAuthorities.Section10}
          />
        </fieldset>
        <div>
          <label className='margin-top-2 margin-bottom-0'>
            If the terms of the Nationwide Permit(s) you want to use require additional information to be included in
            the PCN, please provide here
          </label>
          <Accordion bordered items={nwpTermsAccordionList} />
          <TextAreaInput
            className='margin-top-1'
            name='navigableWatersAdditionalInfo'
            onChange={handleChange}
            readOnly={isReadOnly}
          />
        </div>
        <div>
          <SelectInput
            className='width-50'
            fullWidth={false}
            name='isAnyWorkComplete'
            label='Is any portion of the Nationwide Permit activity complete?'
            tooltip='Describe any work that has already been completed for the NWP activity.'
            onChange={handleChange}
            readOnly={isReadOnly}
            required
          >
            <option key='2' value='true'>
              Yes
            </option>
            <option key='3' value='false'>
              No
            </option>
          </SelectInput>
        </div>
        {(isAnyWorkComplete === 'true' || isAnyWorkComplete === true) && (
          <div>
            <TextAreaInput
              name='completedWorkDescription'
              label='Describe the completed work'
              onChange={handleChange}
              readOnly={isReadOnly}
              required
            />
          </div>
        )}

        {/* Dredge Project Section */}
        {applicableStatutoryAuthority.length > 0 &&
          applicableStatutoryAuthority?.includes(ApplicableStatutoryAuthorities.Section10) && (
            <Section label='Dredging'>
              <div className='text-bold'>Projects Involving Impacts to Section 10 Navigable Waters</div>
              <div>
                <SelectInput
                  className='width-50'
                  fullWidth={false}
                  name='isDredgingNavWaters'
                  label='Does the project involve dredging in navigable waters of the United States?'
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  required
                >
                  <option key='2' value='true'>
                    Yes
                  </option>
                  <option key='3' value='false'>
                    No
                  </option>
                </SelectInput>
              </div>
              {(isDredgingNavWaters === 'true' || isDredgingNavWaters === true) && (
                <PermitsDredgeProject handleChange={handleChange} isReadOnly={isReadOnly} />
              )}
            </Section>
          )}
        <div className='margin-bottom-3' />
      </FormProvider>
    );
  }
);

PcnPermitInformation.metadata = PcnPermitInformationMetadata;

export default PcnPermitInformation;
